import { Box, Grid, Typography, Button } from "@mui/material";
import { capitalize, startCase } from "lodash";
import React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { IPD, IpdRecord, ExtendedIpd } from "../../interfaces/IpdInterface";
import ClientCreateEdit from "../Client/ClientCreateEdit";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import { ageFormatter } from "../../helpers/formatters";
import { getGenderCode } from "../Billing/PrintBill/EightyMmPrint/ClientInfo";
import { tl } from "../../components/translate";
import { formatIpdToExtended } from "./IpdList";

const getPatientNameForIpd = (ipdRecord: IpdRecord): string => {
  if (ipdRecord) {
    return startCase(
      `${ipdRecord.patientRelatedInformation.firstName} ${ipdRecord.patientRelatedInformation.lastName}`
    );
  }
  return "";
};
const IpdDetails = ({ ipd: rawIpd }: { ipd: ExtendedIpd | IPD }): React.ReactNode => {
  const [showMore, setShowMore] = React.useState<boolean>(false);
  const [clientEditMode, setClientEditMode] = React.useState(false);
  const [showClientInfoPanel, setShowClientInfoPanel] = React.useState(false);

  const ipd = React.useMemo(() => {
    if (!rawIpd) return rawIpd as ExtendedIpd;
    if ("bedCode" in rawIpd) {
      return rawIpd as ExtendedIpd;
    }
    return formatIpdToExtended(rawIpd as IPD);
  }, [rawIpd]);

  if (!ipd) return null;

  return (
    <>
      <Box>
        <Grid size={12}>
          <Typography
            variant="h6"
            style={{ fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setShowClientInfoPanel(true);
            }}
          >
            {getPatientNameForIpd(ipd?.ipdRecord).toUpperCase()}
          </Typography>
          <Box display="flex" fontSize="14px">
            <Typography style={{ width: "200px", fontWeight: 600 }}>
              {tl("admissionNumber")}
            </Typography>
            <Typography>{ipd?.ipdNumber}</Typography>
          </Box>
          <Grid size={12}>
            {ipd?.referenceNumber && (
              <Box display="flex" fontSize="14px">
                <Typography sx={{ width: "200px", fontWeight: 600 }}>
                  File/Reference Number
                </Typography>
                <Typography>{ipd.referenceNumber}</Typography>
              </Box>
            )}
            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>{tl("bedNumber")}</Typography>
              <Typography>{ipd?.bedNumber || "-"}</Typography>
            </Box>
            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>
                {tl("wardNumber")}
              </Typography>
              <Typography>{ipd?.wardName || "-"}</Typography>
            </Box>
            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>{tl("roomType")}</Typography>
              <Typography>{ipd?.roomType || "-"}</Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>Doctor in Charge</Typography>
              <Typography>
                {startCase(
                  `${ipd?.doctorInCharge?.firstName || ""} ${
                    ipd?.doctorInCharge?.lastName || ""
                  }`.trim()
                )}
              </Typography>
            </Box>
            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>
                {tl("ipd.admissionCase")}
              </Typography>
              <Typography>{ipd?.ipdRecord?.admissionCondition}</Typography>
            </Box>

            <Box display="flex" fontSize="14px">
              <Typography style={{ width: "200px", fontWeight: 600 }}>
                {tl("ipd.currentCase")}
              </Typography>
              <Typography>{ipd?.currentCase ?? ipd?.ipdRecord?.case}</Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            sx={{ alignSelf: "flex-start" }}
            size="small"
            variant="outlined"
            onClick={() => setShowMore((prev) => !prev)}
            endIcon={showMore ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          >
            {tl("showMore")}
          </Button>

          {showMore && (
            <Box>
              <Grid size={12}>
                <Box width="100%">
                  <Typography variant="h6" style={{ color: "gray" }}>
                    {tl("clientInformation")}
                  </Typography>
                  <Box display="flex" fontSize="14px">
                    <Typography width="200px" style={{ fontWeight: 600 }}>
                      {tl("clientName")}
                    </Typography>
                    <Typography
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => {
                        setShowClientInfoPanel(true);
                      }}
                    >
                      {`${capitalize(
                        ipd?.ipdRecord?.patientRelatedInformation.firstName
                      )} ${capitalize(ipd?.ipdRecord?.patientRelatedInformation.lastName)}`}
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Typography width="200px">{tl("bloodGroup")}</Typography>
                    <Typography>{ipd?.client?.bloodGroup || ""}</Typography>
                  </Box>
                  {ipd?.client?.ssfId && (
                    <Box display="flex">
                      <Typography width="200px">SsfId</Typography>
                      <Typography>{ipd.client.ssfId}</Typography>
                    </Box>
                  )}
                  <Box display="flex">
                    <Typography width="200px">{tl("age/sex")}</Typography>
                    <Typography>
                      {ipd?.client?.dob ? ageFormatter(new Date(ipd?.client?.dob)) : "-"}
                      {ipd?.client?.gender ? `/${getGenderCode(ipd?.client?.gender)}` : "/-"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={12}>
                <Box marginTop="12px" width="100%">
                  <Typography variant="h6" style={{ color: "gray" }}>
                    {tl("otherInformation")}
                  </Typography>
                  <Box display="flex" fontSize="14px" fontWeight={600}>
                    <Typography width="200px" style={{ fontWeight: 600 }}>
                      Provisional Diagnosis
                    </Typography>
                    <Typography>{`${ipd?.ipdRecord?.provisionalDiagnosis}`}</Typography>
                  </Box>
                  <Box display="flex" fontSize="14px" fontWeight={600}>
                    <Typography width="200px" style={{ fontWeight: 600 }}>
                      HOPI
                    </Typography>
                    <Typography>{ipd?.ipdRecord?.hopi}</Typography>
                  </Box>
                  <Box display="flex" fontSize="14px" fontWeight={600}>
                    <Typography width="200px" style={{ fontWeight: 600 }}>
                      {tl("admissionCondition")}
                    </Typography>
                    <Typography>{ipd?.ipdRecord?.admissionCondition}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>

      {showClientInfoPanel &&
        (clientEditMode ? (
          <ClientCreateEdit
            setEditMode={setClientEditMode}
            clientId={ipd?.clientId}
            mode="edit"
            onCancel={() => {
              setClientEditMode(false);
            }}
            stayOnCurrentPage
          />
        ) : (
          <ClientInfo
            wrapperStyle={{ minWidth: "796px", maxWidth: "796px" }}
            id={ipd?.clientId}
            handleViewClose={() => setShowClientInfoPanel(false)}
            setEditMode={setClientEditMode}
            stayOnCurrentPage
          />
        ))}
    </>
  );
};

export default IpdDetails;
